/**
 * System Overview Tab - Extracted from admin.tsx per ADR-039: Component Size Strategy
 * This component displays high-level statistics and metrics for the RBAC system
 */

import type { UserWithRoles } from "@/types/models"
import {
  Box,
  Card,
  Flex,
  Heading,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react"
import { FiKey, FiShield, FiUsers } from "react-icons/fi"

import { useRoles } from "@/hooks/api/useRoles"
import { useUsersWithRoles } from "@/hooks/api/useUsers"
import { useRBACPermissions } from "@/hooks/usePermissions"

export function SystemOverviewTab() {
  const { data: roles = [] } = useRoles()
  const { permissions = [] } = useRBACPermissions()
  const { data: usersWithRoles = [] } = useUsersWithRoles()

  const stats = {
    totalUsers: usersWithRoles?.length || 0,
    activeUsers:
      usersWithRoles?.filter((u: UserWithRoles) => u.is_active)?.length || 0,
    totalRoles: roles.length,
    systemRoles: roles.filter((r) => r.is_system_role).length,
    customRoles: roles.filter((r) => !r.is_system_role).length,
    totalPermissions: (permissions || []).length,
    systemPermissions: (permissions || []).filter((p) => p.is_system_permission)
      .length,
  }

  return (
    <Stack gap={6} align="stretch">
      <Box>
        <Heading size="md" mb={2}>
          System Overview
        </Heading>
        <Text color="gray.600">
          High-level statistics and metrics for the RBAC system
        </Text>
      </Box>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
        <Card.Root>
          <Card.Header>
            <Flex gap={2} align="center">
              <FiUsers />
              <Heading size="md">Users</Heading>
            </Flex>
          </Card.Header>
          <Card.Body>
            <Stack gap={3} align="stretch">
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>
                  Total Users
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {stats.totalUsers}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>
                  Active Users
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="green.500">
                  {stats.activeUsers}
                </Text>
              </Box>
            </Stack>
          </Card.Body>
        </Card.Root>

        <Card.Root>
          <Card.Header>
            <Flex gap={2} align="center">
              <FiShield />
              <Heading size="md">Roles</Heading>
            </Flex>
          </Card.Header>
          <Card.Body>
            <Stack gap={3} align="stretch">
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>
                  Total Roles
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {stats.totalRoles}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>
                  System Roles
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                  {stats.systemRoles}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>
                  Custom Roles
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="purple.500">
                  {stats.customRoles}
                </Text>
              </Box>
            </Stack>
          </Card.Body>
        </Card.Root>

        <Card.Root>
          <Card.Header>
            <Flex gap={2} align="center">
              <FiKey />
              <Heading size="md">Permissions</Heading>
            </Flex>
          </Card.Header>
          <Card.Body>
            <Stack gap={3} align="stretch">
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>
                  Total Permissions
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {stats.totalPermissions}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>
                  System Permissions
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                  {stats.systemPermissions}
                </Text>
              </Box>
            </Stack>
          </Card.Body>
        </Card.Root>
      </SimpleGrid>
    </Stack>
  )
}
